/**
 * RecentDonations.tsx - Recent Donations Feed Component
 *
 * This component displays a feed of recent donations for a fundraiser,
 * showing donor information, amounts, and timestamps in a clean list format.
 *
 * Key Features:
 * - Recent donations list with donor names and amounts
 * - Anonymous donation support and display
 * - Relative timestamp formatting (e.g., "2 hours ago")
 * - Configurable number of donations to display
 * - Empty state handling for no donations
 * - Responsive design for different container sizes
 *
 * Display Options:
 * - Configurable donation count from builder settings
 * - Anonymous donor name handling
 * - Currency formatting for donation amounts
 * - Time-based sorting (most recent first)
 *
 * Props: None (uses MobX store for donation data)
 *
 * Dependencies: MobX store, styled-components, Lucide icons
 */

import React from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { Clock, DollarSign } from "lucide-react";
import { fundraiserStore } from "../stores/FundraiserStore";
import { FundraisersDonationsBoardResponse } from "../models";

const DonationsContainer = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f5f9;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const Title = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

interface RecentDonationsProps {
  promoterId?: string;
  donations: FundraisersDonationsBoardResponse[];
}

const DonationsList = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ScrollWrapper = styled.div`
  max-height: none;
  overflow-y: visible;
  padding-right: 12px;
  margin-right: -12px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
`;

const DonationItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;

  @media (max-width: 768px) {
    gap: 10px;
  }
`;

const DonorAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #dbeafe;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3b82f6;
  font-weight: 700;
  font-size: 14px;
  flex-shrink: 0;
`;

const DonationContent = styled.div`
  flex: 1;
`;

const DonationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
`;

const DonorName = styled.span`
  font-weight: 600;
  color: #1e293b;
  font-size: 14px;
`;

const DonationAmount = styled.span`
  font-weight: 700;
  color: #10b981;
  font-size: 14px;
`;

const DonationComment = styled.p`
  color: #64748b;
  font-size: 13px;
  line-height: 1.4;
  margin: 4px 0 0 0;
  font-style: italic;
`;

const TimeStamp = styled.div`
  color: #94a3b8;
  font-size: 11px;
  margin-top: 4px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px 20px;
  color: #64748b;
  min-height: 200px;
`;

const EmptyStateIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  color: #94a3b8;
`;

const EmptyStateTitle = styled.h4`
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 8px 0;
`;

const EmptyStateText = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
`;

export const RecentDonations: React.FC<RecentDonationsProps> = observer(
  ({ promoterId, donations }) => {
    // const fundraiserId = fundraiserStore.selectedFundraiserId;
    // const sortedDonations = promoterId
    //   ? fundraiserStore.getPromoterDonations(promoterId, fundraiserId)
    //   : fundraiserStore.getFundraiserDonations(fundraiserId);

    // Use live builder data when available, otherwise use defaults
    const selectedFundraiser = fundraiserStore.selectedFundraiser;
    const builderData = fundraiserStore.builderData;
    const hasLiveBuilderData =
      builderData.id &&
      selectedFundraiser &&
      builderData.id === selectedFundraiser.id;

    const recentDonationsCount = hasLiveBuilderData
      ? builderData.recentDonationsCount || 10
      : 10;
    const showDonationComments = hasLiveBuilderData
      ? builderData.showDonationComments || false
      : false;

    const hasDonations = donations?.length > 0; //sortedDonations.length > 0;

    const formatCurrency = (amount: number) => {
      // Cents only when there are any: a $0.25 gift used to show as "$0" and $121.45 as "$121".
      const hasCents = Math.round(amount * 100) % 100 !== 0;
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: hasCents ? 2 : 0,
        maximumFractionDigits: hasCents ? 2 : 0,
      }).format(amount);
    };

    const formatTime = (date: Date) => {
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);

      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins} minutes ago`;

      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours} hours ago`;

      const diffDays = Math.floor(diffHours / 24);
      return `${diffDays} days ago`;
    };

    const getInitials = (name: string) => {
      if (!name || name === "Anonymous Donor") {
        return "AD";
      }
      return name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase();
    };

    // const getDisplayName = (donation: any) => {
    //   return donation.isAnonymous ? 'Anonymous Donor' : donation.donorName;
    // };

    const topDonations = donations.slice(0, recentDonationsCount);
    return (
      <DonationsContainer>
        <Title>
          <Clock size={20} className="text-blue-500" />
          {promoterId ? "My Donations" : "Donations"}
        </Title>

        <ScrollWrapper>
          {!hasDonations ? (
            <EmptyState>
              <EmptyStateIcon>
                <DollarSign size={24} />
              </EmptyStateIcon>
              <EmptyStateTitle>No Donations Yet</EmptyStateTitle>
              <EmptyStateText>
                Recent donations will appear here once the first donation is
                received.
              </EmptyStateText>
            </EmptyState>
          ) : (
            <DonationsList>
              {topDonations.map((donation, idx) => (
                <DonationItem key={idx}>
                  <DonorAvatar>
                    {getInitials(donation.getDisplayName())}
                  </DonorAvatar>

                  <DonationContent>
                    <DonationHeader>
                      <DonorName>{donation.getDisplayName()}</DonorName>
                      <DonationAmount>
                        {formatCurrency(donation.amount)}
                      </DonationAmount>
                    </DonationHeader>

                    {showDonationComments && donation.comment && (
                      <DonationComment>{donation.comment}</DonationComment>
                    )}

                    <TimeStamp>{formatTime(donation.donatedOn)}</TimeStamp>
                  </DonationContent>
                </DonationItem>
              ))}
            </DonationsList>
          )}
        </ScrollWrapper>
      </DonationsContainer>
    );
  }
);
